import { range } from 'd3-array';
import { line } from 'd3-shape';
import {
  PRESSURE_LINES,
  KELVIN,
  POISSON,
  BREAKING_PRESSURE,
} from './constants.js';

/**
 * Draw the skew-T background grid: clipping path, isotherms, isobars, dry adiabats, and axes.
 * @param {import('d3-selection').Selection} skewtbg - Background <g> element
 * @param {import('./scales.js').Scales} scales
 * @param {string} clipId - Unique clip-path ID for this instance
 */
export function drawBackground(skewtbg, scales, clipId) {
  const { w, h, y, skewX, basep, topp, xAxis, yAxis, yAxis2 } = scales;
  const clipUrl = `url(#${clipId})`;

  // Clipping path
  skewtbg
    .append('clipPath')
    .attr('id', clipId)
    .append('rect')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', w)
    .attr('height', h);

  // Skewed temperature lines (isotherms).
  // Skysight draws these as a 3-point polyline (top, break, base) so that the
  // PIECEWISE skewX produces the visible kink at BREAKING_PRESSURE. Source:
  //   g.selectAll("templine").data(N(-100,45,10)).enter().append("polyline")
  //     .attr("points", e => `${skewX(e,topp)},${y(topp)} ${skewX(e,break)},${y(break)} ${skewX(e,basep)},${y(basep)}`)
  skewtbg
    .selectAll('.templine')
    .data(range(-100, 45, 10))
    .enter()
    .append('polyline')
    .attr('points', (d) => {
      const p1 = `${skewX(d, topp) - 0.5},${y(topp)}`;
      const p2 = `${skewX(d, BREAKING_PRESSURE) - 0.5},${y(BREAKING_PRESSURE)}`;
      const p3 = `${skewX(d, basep) - 0.5},${y(basep)}`;
      return `${p1} ${p2} ${p3}`;
    })
    .attr('class', (d) => (d === 0 ? 'skewt-tempzero' : 'skewt-gridline'))
    .style('fill', 'none')
    .attr('clip-path', clipUrl);

  // Logarithmic pressure lines (isobars). Skysight filters out any isobar
  // that lies outside [topp, basep] so stray lines don't render above the
  // chart top or below the chart bottom. Source: docs/skysight-skewt-bundle.js
  //   this.plines = [1000, 850, 700, 500, 300, 200, 100].filter(c => c <= basep && c >= topp)
  const visiblePLines = PRESSURE_LINES.filter((p) => p <= basep && p >= topp);
  skewtbg
    .selectAll('.pressureline')
    .data(visiblePLines)
    .enter()
    .append('line')
    .attr('x1', 0)
    .attr('x2', w)
    .attr('y1', (d) => y(d))
    .attr('y2', (d) => y(d))
    .attr('class', 'skewt-gridline');

  // Dry adiabats — Skysight samples pressure every 10 hPa from topp to basep
  // and uses skewX so the curve naturally bends at BREAKING_PRESSURE.
  // Source: const t = N(this.topp, this.basep + 1, 10), s = N(-30, 240, 20),
  //         d = R().curve(H).x((e,c)=>skewX(((273.15+e)/(1000/t[c])^0.286 - 273.15), t[c])).y((e,c)=>y(t[c]))
  const pp = range(topp, basep + 1, 10);
  const dryad = range(-30, 240, 20);
  const all = dryad.map((theta) => pp.map(() => theta));

  /** @type {import('d3-shape').Line<number>} */
  const dryline = /** @type {any} */ (line())
    .x((/** @type {number} */ d, /** @type {number} */ i) => {
      const tempK = (KELVIN + d) / Math.pow(1000 / pp[i], POISSON) - KELVIN;
      return skewX(tempK, pp[i]);
    })
    .y((/** @type {number} */ _d, /** @type {number} */ i) => y(pp[i]));

  skewtbg
    .selectAll('.dryadiabatline')
    .data(all)
    .enter()
    .append('path')
    .attr('class', 'skewt-gridline')
    .attr('clip-path', clipUrl)
    .attr('d', /** @type {any} */ (dryline));

  // Right edge line
  skewtbg
    .append('line')
    .attr('x1', w - 0.5)
    .attr('x2', w - 0.5)
    .attr('y1', 0)
    .attr('y2', h)
    .attr('class', 'skewt-gridline');

  // Axes
  skewtbg
    .append('g')
    .attr('class', 'skewt-axis skewt-x')
    .attr('transform', `translate(0,${h - 0.5})`)
    .call(/** @type {any} */ (xAxis));

  skewtbg
    .append('g')
    .attr('class', 'skewt-axis skewt-y')
    .attr('transform', 'translate(-0.5,0)')
    .call(/** @type {any} */ (yAxis));

  skewtbg
    .append('g')
    .attr('class', 'skewt-axis skewt-y skewt-ticks')
    .attr('transform', 'translate(-0.5,0)')
    .call(/** @type {any} */ (yAxis2));
}
